import React, { useEffect, useState, createElement } from 'react';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, ShieldCheck, Lock, ArrowRight, FileText, PhoneCall, Download, X } from 'lucide-react';
const CTAButton = ({
  className = '',
  href = '#checkout',
  external = false
}: {
  className?: string;
  href?: string;
  external?: boolean;
}) => <div className={`flex flex-col items-center ${className}`} data-id="element-0">
    <a href={href} {...external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-5 text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-lg shadow-lg" data-id="element-1">
      DOWNLOAD IT NOW
      <ArrowRight className="ml-3 w-6 h-6" data-id="element-2" />
    </a>
    <div className="flex items-center mt-3 text-sm font-medium opacity-80" data-id="element-3">
      <Lock className="w-4 h-4 mr-1.5" data-id="element-4" />
      Secure checkout. Digital delivery.
    </div>
  </div>;
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-id="element-5">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-6">
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200" data-id="element-7">
          <h2 className="font-serif text-2xl font-bold text-brand-dark" data-id="element-8">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-9">
            <X className="w-6 h-6 text-gray-500" data-id="element-10" />
          </button>
        </div>
        <div className="px-8 py-6 overflow-y-auto max-h-[70vh] text-gray-700 text-base leading-relaxed space-y-4" data-id="element-11">
          {children}
        </div>
      </div>
    </div>;
};
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-300 last:border-0" data-id="element-12">
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full py-6 text-left focus:outline-none" aria-expanded={isOpen} data-id="element-13">
        <span className="font-serif text-xl md:text-2xl font-bold text-brand-dark pr-4" data-id="element-14">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-15" /> : <ChevronDown className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-16" />}
      </button>
      {isOpen && <div className="pb-6 text-lg md:text-xl leading-relaxed text-gray-700" data-id="element-17">
          {answer}
        </div>}
    </div>;
};
export function App() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  useEffect(() => {
    document.title = 'The Senior Benefit Unlocker';
    // Update meta tags
    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    setMeta('og:title', 'The Senior Benefit Unlocker');
    setMeta('og:description', 'Reclaim the $400 to $1,200 in monthly credits the system owes you.');
    setMeta('description', 'Reclaim the $400 to $1,200 in monthly credits the system owes you.');
  }, []);
  return <div className="font-sans text-gray-800 bg-white selection:bg-brand-gold selection:text-brand-dark" data-id="element-18">
      {/* 1. HERO SECTION */}
      <section className="bg-brand-dark text-white pt-16 pb-20 md:pt-24 md:pb-28 px-6" data-id="element-19">
        <div className="max-w-6xl mx-auto" data-id="element-20">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-21">
            <div className="flex-1 text-center lg:text-left" data-id="element-22">
              <div className="inline-block px-4 py-1.5 mb-6 text-sm md:text-base font-bold tracking-wider text-brand-dark bg-brand-gold rounded-full uppercase" data-id="element-23">
                New Release for 2026
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-24">
                The Senior Benefit Unlocker
              </h1>
              <h2 className="text-xl md:text-2xl font-medium leading-relaxed text-gray-300 mb-10" data-id="element-25">
                Stop choosing between groceries and electricity—reclaim the{' '}
                <span className="text-brand-gold font-bold" data-id="element-26">
                  $400 to $1,200
                </span>{' '}
                in monthly credits the system owes you.
              </h2>
              <CTAButton className="lg:items-start" data-id="element-27" />
            </div>
            <div className="flex-1 w-full max-w-lg lg:max-w-none" data-id="element-28">
              <img src="https://cdn.magicpatterns.com/uploads/gpWjbD8KhtwUVrhi7qUaGc/The_Senior_Benefit_Unlocker_main_image.png" alt="Senior woman smiling while reading The Senior Benefit Unlocker on a tablet" className="w-full h-auto rounded-xl shadow-2xl border-4 border-brand-gold/20" data-id="element-29" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-30">
        <div className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-6" data-id="element-31">
          <p className="font-serif text-2xl md:text-3xl font-bold text-brand-dark leading-snug mb-8" data-id="element-32">
            You worked for forty years. You paid your taxes, raised your family,
            and played by the rules.
          </p>
          <p data-id="element-33">
            You were promised a "golden age" of security, but instead, you're
            sitting at the kitchen table at 11 PM wondering how $42 is supposed
            to last nine more days.
          </p>
          <p data-id="element-34">
            It feels like a knot in your chest that never goes away. You're
            scanning the grocery aisles for the cheapest eggs and keeping the
            heat at 65 degrees just to survive.
          </p>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="bg-white py-20 md:py-28 px-6" data-id="element-35">
        <div className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-6" data-id="element-36">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-8 text-center" data-id="element-37">
            The System Isn't Broken—It's Designed to Be a Maze
          </h2>
          <p data-id="element-38">
            The truth is, inflation has turned your fixed Social Security check
            into a "pay cut." While prices for milk, meds, and power have
            skyrocketed, your income has stayed nearly flat.
          </p>
          <p data-id="element-39">
            You've tried cutting back. You've tried generic coupons. You've even
            tried calling the main Social Security line, only to be put on hold
            for two hours and told you make "too much" to get help.
          </p>
          <p className="font-medium text-brand-dark p-6 bg-gray-50 border-l-4 border-brand-gold rounded-r-lg mt-8" data-id="element-40">
            It's built to be so frustrating and bureaucratic that most seniors
            simply give up and stop asking for the benefits they already paid
            for.
          </p>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-41">
        <div className="max-w-4xl mx-auto" data-id="element-42">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-43">
            Why Everything You've Tried Has Failed
          </h2>
          <div className="grid gap-6 md:grid-cols-2" data-id="element-44">
            {['General "budgeting" advice is insulting when your fixed income doesn\'t cover your basic fixed bills.', 'Official government websites are written in "legalese" specifically to hide the eligibility rules.', "Local charities are often overwhelmed and don't tell you about the long-term federal credits you actually qualify for.", 'Most "Senior Discount" lists only save you a few cents on coffee, which doesn\'t solve a $500 monthly gap.'].map((text, i) => <div key={i} className="flex items-start bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-45">
                <XCircle className="w-8 h-8 text-red-500 flex-shrink-0 mr-4 mt-1" data-id="element-46" />
                <p className="text-lg text-gray-700 leading-relaxed" data-id="element-47">{text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="bg-brand-dark text-white py-20 md:py-28 px-6" data-id="element-48">
        <div className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-8 text-center" data-id="element-49">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-gold mb-6" data-id="element-50">
            The "State-Federal Benefit Gap" Audit
          </h2>
          <p className="text-gray-300" data-id="element-51">
            This is a targeted framework that ignores the "front door" of the
            welfare office. Instead, it identifies specific, non-taxable
            "Micro-Credits" at the state and county level that stack together.
          </p>
          <p className="text-gray-300" data-id="element-52">
            By using the "Standard Medical Deduction" trick and "Broad-Based
            Categorical Eligibility," you can often unlock hundreds of dollars
            in food, utility, and medical premium relief—
            <strong className="text-white" data-id="element-53">
              effectively giving yourself a 20% to 30% raise on your Social
              Security check overnight.
            </strong>
          </p>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="bg-white py-20 md:py-28 px-6" data-id="element-54">
        <div className="max-w-4xl mx-auto" data-id="element-55">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-56">
            Here Is Exactly What You Get
          </h2>
          <div className="space-y-4" data-id="element-57">
            {[{
            title: 'The Full Step-by-Step PDF Guide',
            desc: "The complete 'Senior Benefit Unlocker' manual."
          }, {
            title: "The 'Invisible Check' Roadmap",
            desc: 'How to find the $400–$1,200 in monthly credits waiting for you.'
          }, {
            title: 'The LIHEAP Hack',
            desc: 'How to get your utility bills paid and your home sealed for free.'
          }, {
            title: 'The Medicare Part B Refund',
            desc: 'How to get the $174.70 premium put back into your check every month.'
          }, {
            title: "The 'Bureaucracy Scripts'",
            desc: "Exactly what to say on the phone to get a 'Yes' from state agencies."
          }, {
            title: 'The 15-Minute Benefit Audit Worksheet',
            desc: 'See exactly how much you can reclaim.'
          }].map((item, i) => <div key={i} className="flex items-start p-6 bg-brand-cream rounded-xl" data-id="element-58">
                <CheckCircle2 className="w-8 h-8 text-brand-gold flex-shrink-0 mr-4 mt-0.5" data-id="element-59" />
                <div data-id="element-60">
                  <h3 className="font-bold text-xl text-brand-dark mb-1" data-id="element-61">
                    {item.title}
                  </h3>
                  <p className="text-lg text-gray-700" data-id="element-62">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-63">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-64">
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-8 border-brand-gold" data-id="element-65">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-brand-dark mb-8" data-id="element-66">
              Who This Is For
            </h3>
            <ul className="space-y-6" data-id="element-67">
              {['US Retirees living on a fixed Social Security income.', 'Seniors who feel like they are "falling through the cracks" of the system.', 'Homeowners 62+ who are worried about rising property taxes.', 'Anyone who has been told they "make too much" for traditional welfare but still can\'t afford their meds.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-68">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mr-3 mt-1" data-id="element-69" />
                  <span className="text-lg text-gray-700" data-id="element-70">{text}</span>
                </li>)}
            </ul>
          </div>

          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-8 border-brand-dark" data-id="element-71">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-brand-dark mb-8" data-id="element-72">
              Who This Is NOT For
            </h3>
            <ul className="space-y-6" data-id="element-73">
              {['High-net-worth retirees with large investment portfolios.', 'People looking for "get rich" schemes or illegal tax dodges.', 'Those unwilling to spend 15 minutes filling out a form to save $174 a month.'].map((text, i) => <li key={i} className="flex items-start" data-id="element-74">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mr-3 mt-1" data-id="element-75" />
                  <span className="text-lg text-gray-700" data-id="element-76">{text}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="bg-white py-20 md:py-28 px-6" data-id="element-77">
        <div className="max-w-5xl mx-auto" data-id="element-78">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-16 text-center" data-id="element-79">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-10 relative" data-id="element-80">
            {/* Desktop connecting line */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gray-200 z-0" data-id="element-81"></div>

            {[{
            icon: Download,
            title: '1. Download the Guide',
            desc: 'Instantly access the Audit Checklist on your computer, tablet, or phone.'
          }, {
            icon: FileText,
            title: '2. Run the 15-Minute Audit',
            desc: 'Follow the simple steps to see which specific credits are available in your zip code.'
          }, {
            icon: PhoneCall,
            title: '3. Use the Scripts',
            desc: 'Call the local numbers provided and read the scripts to start your applications.'
          }].map((step, i) => <div key={i} className="relative z-10 flex flex-col items-center text-center bg-white" data-id="element-82">
                <div className="w-24 h-24 bg-brand-cream rounded-full flex items-center justify-center mb-6 border-4 border-white shadow-md" data-id="element-83">
                  <step.icon className="w-10 h-10 text-brand-gold" data-id="element-84" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-brand-dark mb-4" data-id="element-85">
                  {step.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed" data-id="element-86">
                  {step.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. PROOF / BELIEVABILITY */}
      <section className="bg-brand-gold py-16 px-6" data-id="element-87">
        <div className="max-w-4xl mx-auto text-center" data-id="element-88">
          <ShieldCheck className="w-16 h-16 text-brand-dark mx-auto mb-6" data-id="element-89" />
          <p className="font-serif text-2xl md:text-3xl font-bold text-brand-dark leading-relaxed" data-id="element-90">
            "Most seniors discover that by stacking just three programs—Utility
            Assistance, the Medicare Premium Refund, and the Renter's
            Credit—they move from a monthly deficit to a{' '}
            <span className="underline decoration-4 underline-offset-4" data-id="element-91">
              $500 monthly surplus
            </span>{' '}
            in less than 30 days."
          </p>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="bg-brand-cream py-20 md:py-28 px-6" data-id="element-92">
        <div className="max-w-3xl mx-auto" data-id="element-93">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-94">
            Frequently Asked Questions
          </h2>
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10 border border-gray-100" data-id="element-95">
            <FAQItem question="Is this legal?" answer="Yes. These are federal and state programs funded by your tax dollars. You have already paid into these systems your entire working life." data-id="element-96" />
            <FAQItem question="Will it lower my Social Security?" answer="No. These benefits are added on top of your retirement check. They do not reduce or interfere with your standard Social Security payouts." data-id="element-97" />
            <FAQItem question="What if I own my home?" answer="You are still eligible for most credits, including energy assistance and property tax freezes designed specifically to keep seniors in their homes." data-id="element-98" />
            <FAQItem question="Is my information safe?" answer="We do not collect your personal data; this guide simply shows you how to apply directly with the government using their secure channels." data-id="element-99" />
          </div>
        </div>
      </section>

      {/* 11. OFFER + PRICE BLOCK & CTA */}
      <section id="checkout" className="bg-white py-20 md:py-28 px-6 border-t border-gray-100" data-id="element-100">
        <div className="max-w-3xl mx-auto text-center" data-id="element-101">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark mb-8" data-id="element-102">
            Reclaim Your Peace of Mind
          </h2>
          <p className="text-xl md:text-2xl text-gray-700 mb-10 leading-relaxed" data-id="element-103">
            For the price of one lunch, get "The Senior Benefit Unlocker" today
            and start bridging the gap between your income and your life.
          </p>

          <div className="bg-brand-cream p-10 rounded-3xl border-2 border-brand-gold max-w-xl mx-auto mb-10" data-id="element-104">
            <div className="text-lg font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-105">
              One-Time Investment
            </div>
            <div className="mb-2" data-id="element-106">
              <span className="text-3xl md:text-4xl font-bold text-gray-400 line-through" data-id="element-107">
                $19
              </span>
            </div>
            <div className="text-lg font-bold text-red-600 uppercase tracking-wider mb-2" data-id="element-108">
              Limited Time Offer
            </div>
            <div className="text-6xl md:text-7xl font-bold text-brand-dark mb-8" data-id="element-109">
              $6.39
            </div>
            <CTAButton href="https://www.shopdilore.com/cart/53835577721142:1" external data-id="element-110" />
          </div>

          <div className="flex items-center justify-center gap-6 opacity-60 grayscale" data-id="element-111">
            {/* Placeholder for trust badges if needed, using text for now */}
            <span className="font-bold text-sm uppercase tracking-wider" data-id="element-112">
              100% Secure
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" data-id="element-113"></span>
            <span className="font-bold text-sm uppercase tracking-wider" data-id="element-114">
              Instant Access
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" data-id="element-115"></span>
            <span className="font-bold text-sm uppercase tracking-wider" data-id="element-116">
              Satisfaction Guaranteed
            </span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-brand-dark text-gray-400 py-10 text-center text-sm px-6" data-id="element-117">
        <p data-id="element-118">
          © {new Date().getFullYear()} Dilore Publishing. All rights reserved.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4" data-id="element-119">
          <button onClick={() => setPrivacyOpen(true)} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-120">
            Privacy Policy
          </button>
          <span className="w-1 h-1 rounded-full bg-gray-500" data-id="element-121"></span>
          <button onClick={() => setTermsOpen(true)} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-122">
            Terms of Service
          </button>
        </div>
        <p className="mt-4 max-w-2xl mx-auto opacity-60" data-id="element-123">
          Disclaimer: This guide provides educational information and is not
          legal or financial advice. Results vary based on individual
          circumstances and state-specific program funding.
        </p>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-124">
        <p data-id="element-125">
          <strong data-id="element-126">Last Updated:</strong> March 2026
        </p>
        <p data-id="element-127">
          Dilore Publishing ("we," "us," or "our") is committed to protecting
          your privacy. This Privacy Policy explains how we handle information
          when you purchase and use The Senior Benefit Unlocker guide.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-128">
          Information We Collect
        </h3>
        <p data-id="element-129">
          We collect only the minimum information necessary to process your
          purchase, including your email address and payment details. Payment
          processing is handled securely by our third-party payment processor —
          we never store your credit card information.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-130">
          How We Use Your Information
        </h3>
        <p data-id="element-131">
          Your email address is used solely to deliver your digital purchase and
          provide order-related communications. We do not sell, rent, or share
          your personal information with third parties for marketing purposes.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-132">
          Data Security
        </h3>
        <p data-id="element-133">
          We use industry-standard security measures to protect your
          information. All transactions are encrypted using SSL technology.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-134">Cookies</h3>
        <p data-id="element-135">
          Our website may use essential cookies to ensure proper functionality.
          We do not use tracking cookies for advertising purposes.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-136">Your Rights</h3>
        <p data-id="element-137">
          You may request access to, correction of, or deletion of your personal
          data at any time. This guide does not collect your Social Security
          number, medical records, or any government benefit information — it
          simply provides educational instructions for you to apply directly
          with government agencies.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-138">
          Changes to This Policy
        </h3>
        <p data-id="element-139">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date.
        </p>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" data-id="element-140">
        <p data-id="element-141">
          <strong data-id="element-142">Last Updated:</strong> March 2026
        </p>
        <p data-id="element-143">
          By purchasing and using The Senior Benefit Unlocker ("the Guide"), you
          agree to the following terms and conditions.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-144">
          Product Description
        </h3>
        <p data-id="element-145">
          The Guide is a digital educational resource that provides information
          about publicly available government benefit programs. It is delivered
          electronically upon purchase.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-146">
          Educational Purpose Only
        </h3>
        <p data-id="element-147">
          The Guide is for informational and educational purposes only. It does
          not constitute legal, financial, or tax advice. We recommend
          consulting with a qualified professional regarding your specific
          situation. Eligibility for government programs varies by state,
          county, and individual circumstances.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-148">
          No Guarantees
        </h3>
        <p data-id="element-149">
          While the Guide provides actionable steps to identify potential
          benefits, we cannot guarantee specific dollar amounts or approval for
          any government program. Results vary based on individual eligibility,
          location, and program funding availability.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-150">
          Digital Delivery
        </h3>
        <p data-id="element-151">
          The Guide is a digital product delivered immediately upon purchase.
          Due to the nature of digital products, all sales are final. If you
          experience technical issues accessing your purchase, please reach out
          and we will ensure you receive your product.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-152">
          Intellectual Property
        </h3>
        <p data-id="element-153">
          All content within the Guide is the intellectual property of Dilore
          Publishing. You may not reproduce, distribute, or resell the Guide
          without written permission.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-154">
          Limitation of Liability
        </h3>
        <p data-id="element-155">
          Dilore Publishing shall not be held liable for any outcomes resulting
          from the use of information provided in the Guide. Your use of the
          Guide and any actions taken based on its contents are at your own
          discretion and risk.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-156">
          Governing Law
        </h3>
        <p data-id="element-157">
          These terms shall be governed by and construed in accordance with the
          laws of the United States.
        </p>
      </Modal>
    </div>;
}